import { Injectable } from "@angular/core";
import { TranslocoService } from "@ngneat/transloco";
import { Client } from "@stomp/stompjs";
import { ConfigurationService } from "app/core/config/configuration.service";
import { AlertType, BiAlert } from "app/core/models/bi-alert.model";
import { BIAlertService } from "app/core/services/bi-alert.service";
import { LoggerService } from "app/core/services/logger.service";
import { UserDetailsService } from "app/core/services/user-details.service";
import { Notification } from "app/layout/common/notifications/notifications.types";
import moment from "moment";
import { NgEventBus } from "ng-event-bus";
import { map, Observable, of, ReplaySubject, Subject, switchMap, take, tap, catchError } from "rxjs";
import SockJS from "sockjs-client";
import { WSAlertMessage } from "./ws-alert-message.type";
import { RoleConstants } from "app/core/constants/role-constants";

@Injectable({
    providedIn: "root",
})
export class NotificationsService {
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
    private _notificationsUpdated: Subject<void> = new Subject();

    private stompClient: Client;

    /**
     * Constructor
     */
    constructor(
        private configurationService: ConfigurationService,
        private userDetailsService: UserDetailsService,
        private loggerService: LoggerService,
        private biAlertService: BIAlertService,
        private translocoService: TranslocoService,
        private eventBus: NgEventBus
    ) {

        this.stompClient = new Client({
            onConnect: frame => {
                this.loggerService.log("Connected");



                this.stompClient.subscribe("/user/queue/alerts", message => {

                    const alertMessage: WSAlertMessage = JSON.parse(message.body) as WSAlertMessage;

                    const notification = this.messageToNotification(alertMessage);

                    this.eventBus.cast("notification:created", notification);
                   

                });
            },
            onDisconnect: frame => { },
            onWebSocketError: error => {
                console.error("Error with websocket", error);
            },
            onStompError: frame => {
                console.error("Broker reported error: " + frame.headers["message"]);
                console.error("Additional details: " + frame.body);
            },
            debug: str => {
                this.loggerService.debug(str);
            },
        });

        if (this.userDetailsService.currentUser?.login) {
            this.stompClient.webSocketFactory = () => {
                return new SockJS(
                    `${this.configurationService.getConfig().biAlertUrl}/ws?username=${this.userDetailsService.currentUser.login
                    }`
                );
            };
        } else {
            this.loggerService.error("WS failed: user not logged");
        }


    }

    init(): void {
        if (this.userDetailsService.hasAllRoles(RoleConstants.ROLE_ALERT_NOTIF)) {
            this.stompClient.activate();
        }
    }

    destroy(): void {
        if (this.userDetailsService.hasAllRoles(RoleConstants.ROLE_ALERT_NOTIF)) {
            this.stompClient.deactivate();
        }
    }



    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]> {
        return this._notifications.asObservable();
    }

    get notificationsUpdated$(): Observable<void> {
        return this._notificationsUpdated.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notifications
     */
    getAll(): Observable<Notification[]> {
        if (this.userDetailsService.hasAllRoles(RoleConstants.ROLE_ALERT_NOTIF)) {
            return this.biAlertService.findUnreadAlerts().pipe(
                take(1),
                map((response) => {
                    const alerts = response.data;

                    return alerts.map(alert => this.alertToNotification(alert));
                }),
                catchError(error => {
                    this.loggerService.warn(
                        'BI Alert module is unavailable; notifications will not be loaded',
                        error
                    );
                    return of([]);
                })
            );
        }

        return of([]);
    }

    markAsRead(id: string): Observable<Notification> {
        return this.biAlertService.markAlertAsRead(id)
            .pipe(
                take(1),
                map((biAlertUser) => this.alertToNotification(biAlertUser)),
                tap(notification => {
                    this.eventBus.cast("notification:markRead", notification);
                })
            );
        

    }



    delete(notification: Notification): Observable<boolean> {
        return this.biAlertService
            .deleteAlert(notification.id)
            .pipe(
                take(1),
                tap(notification => this.eventBus.cast("notification:delete", notification)),
                map(() => true)
            )
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<boolean> {

        return this.biAlertService.markAllAlertsAsRead()
            .pipe(
                take(1),
                switchMap(() => this.getAll()),
                tap(() => {
                    this.eventBus.cast("notification:markAllRead")
                }),
                map(() => true)
            )

    }

    private alertToNotification(alert: BiAlert): Notification {


        const description = this.getAlertTypeDescription(alert.alertType);

        const notification: Notification = {
            id: alert.id,
            read: alert.read,
            time: moment.utc(alert.createdAt).local().toISOString(),
            title: this.translocoService.translate(`module.biAlert.${alert.alertType}`),
            description: `[${alert.hotelName}] ` + description,
            link: "/bi-alert/view/" + alert.id,
            useRouter: true,
            icon: this.getIcon(alert.criticality),
            iconClass: this.getIconClass(alert.criticality)

        };

        return notification;
    }

    private messageToNotification(alertMessage: WSAlertMessage): Notification {

        const description = this.getAlertTypeDescription(alertMessage.alertType);

        const notification: Notification = {
            id: alertMessage.id,
            time: moment.utc(alertMessage.time).local().toISOString(),
            read: false,
            title: this.translocoService.translate(`module.biAlert.${alertMessage.alertType}`),
            description: `[${alertMessage.hotelName}] ` + description,
            link: "/bi-alert/view/" + alertMessage.id,
            useRouter: true,
            icon: this.getIcon(alertMessage.criticality),
            iconClass: this.getIconClass(alertMessage.criticality)
        };

        return notification;

    }

    private getAlertTypeDescription(alertType: string | AlertType): string {
        let key: string | null = null;
        let description = "";

        switch (alertType) {
            case 'NO_COMM_LOGO':
                key = "module.biAlert.no-comm-notification-message";
                break;
            case 'CONTINUOUS_PRESENCE_LOGO':
                key = "module.biAlert.continuous-presence-notification-message";
                break;
            case 'SENSOR_PROBLEM':
                key = "module.biAlert.sensor-problems-notification-message";
                break;
            case 'NO_ENERGY_CONSUMPTION_DELTA':
                key = "module.biAlert.no-energy-consumption-delta-notification-message";
                break;
            case 'NO_ENERGY_CONSUMPTION_TOTAL':
                key = "module.biAlert.no-energy-consumption-total-notification-message";
                break;
        }


        if (key) {
            description = this.translocoService.translate(key);
        }

        return description;


    }

   
    private getIcon(criticality: string): string {
        switch (criticality) {
            case 'WARNING':
                return 'feather:alert-triangle'; //or circle
            case 'ERROR':
            case 'CRITICAL':
                return 'feather:alert-octagon';
            default:
                return 'feather:info'

        }
    }

    private getIconClass(criticality: string): string {
        switch (criticality) {
            case 'WARNING':
                return 'text-amber-500'; //or circle
            case 'ERROR':
            case 'CRITICAL':
                return 'text-rose-700';
            default:
                return 'text-blue-700'

        }
    }
}
